import React, { createContext, useContext, useEffect, useState, useCallback, useRef } from 'react';
import { User, AuthError } from '@supabase/supabase-js';
import { supabase, UserProfile } from '../lib/supabase';
import { splitFullNameLastWordIsLastName } from '../pages/account/accountShared';
import { supabaseApi } from '../lib/supabaseApi';
import { deleteMemberAccount as deleteMemberAccountRequest } from '../lib/deleteMemberAccount';
import {
  clearMemberInviteCode,
  peekMemberInviteCode,
} from '../lib/memberInviteAttribution';
import {
  wasSignupConfirmationRedirect,
  getSignupConfirmTokens,
  getExpectedSignupConfirmUserId,
  clearCapturedSignupConfirmTokens,
} from '../lib/authEmailConfirm';
import { getSignupConfirmRedirectUrl } from '../lib/authRedirect';
import {
  shouldSkipDuplicateProfileFetch,
  scheduleAfterAuthCallback,
} from '../lib/authProfileFetch';

interface AuthContextType {
  user: User | null;
  profile: UserProfile | null;
  loading: boolean;
  signUp: (email: string, password: string, userData: any) => Promise<{ user: User | null; error: AuthError | null }>;
  signIn: (email: string, password: string) => Promise<{ user: User | null; error: AuthError | null }>;
  signOut: () => Promise<void>;
  /** Permanently deletes the member account server-side, then clears local session. */
  deleteMemberAccount: () => Promise<{ error: string | null }>;
  updateProfile: (updates: Partial<UserProfile>) => Promise<void>;
  markAsLoggedIn: () => Promise<void>;
}

const AuthContext = createContext<AuthContextType | undefined>(undefined);

export const useAuth = () => {
  const context = useContext(AuthContext);
  if (!context) {
    throw new Error('useAuth must be used within an AuthProvider');
  }
  return context;
};

function mapRowToUserProfile(row: any): UserProfile {
  const accountStatus = row.account_status as 'active' | 'deleted' | undefined;
  const deletedAt = row.deleted_at as string | null | undefined;
  return {
    id: row.id,
    email: row.email,
    first_name: row.first_name ?? '',
    last_name: row.last_name ?? '',
    role: row.role,
    timezone: row.timezone,
    has_logged_in: row.has_logged_in ?? false,
    country: row.country,
    referral_source: row.referral_source,
    created_at: row.created_at,
    updated_at: row.updated_at,
    account_status: accountStatus === 'deleted' ? 'deleted' : 'active',
    deleted_at: deletedAt ?? null,
  };
}

/** Derive first/last from auth metadata or split legacy full-name keys. */
function nameFieldsFromAuthMetadata(
  meta: Record<string, unknown> | undefined,
  email: string | undefined,
): { first_name: string; last_name: string } {
  const m = meta || {};
  let fn = typeof m.first_name === 'string' ? m.first_name.trim() : '';
  let ln = typeof m.last_name === 'string' ? m.last_name.trim() : '';
  if (!fn && !ln) {
    const fullDisplay = String(m.full_name || m.name || email?.split('@')[0] || '').trim();
    if (fullDisplay) {
      const split = splitFullNameLastWordIsLastName(fullDisplay);
      fn = split.first;
      ln = split.last;
    }
  }
  return { first_name: fn, last_name: ln };
}

interface AuthProviderProps {
  children: React.ReactNode;
}

export const AuthProvider: React.FC<AuthProviderProps> = ({ children }) => {
  const [user, setUser] = useState<User | null>(null);
  const [profile, setProfile] = useState<UserProfile | null>(null);
  const [loading, setLoading] = useState(true);
  
  // Refs to prevent duplicate fetches
  const profileFetchInProgress = useRef(false);
  const lastFetchedUserId = useRef<string | null>(null);
  const authInitialized = useRef(false);
  const subscriptionSetup = useRef(false);
  const userRef = useRef<User | null>(null);
  const profileRef = useRef<UserProfile | null>(null);
  const fetchUserProfileRef = useRef<((userId: string, retryCount?: number, force?: boolean) => Promise<void>) | null>(null);
  
  // Keep refs in sync with state
  useEffect(() => {
    userRef.current = user;
  }, [user]);
  
  useEffect(() => {
    profileRef.current = profile;
  }, [profile]);

  /** Clears Supabase session, local auth state, and auth storage (shared by signOut and deleted-account handling). */
  const clearAuthSession = useCallback(async () => {
    try {
      console.log('🚪 Signing out...');
      try {
        const { error } = await supabase.auth.signOut();
        if (error) {
          console.warn('⚠️ Error signing out from Supabase (may not have session):', error.message);
        } else {
          console.log('✅ Signed out from Supabase successfully');
        }
      } catch (signOutError: unknown) {
        const msg = signOutError instanceof Error ? signOutError.message : String(signOutError);
        console.warn('⚠️ Supabase signOut error (continuing anyway):', msg);
      }
      setUser(null);
      setProfile(null);
      setLoading(false);
      userRef.current = null;
      profileRef.current = null;
      lastFetchedUserId.current = null;
      profileFetchInProgress.current = false;
      try {
        const supabaseUrl = process.env.REACT_APP_SUPABASE_URL || '';
        const possibleKeys = [
          'supabase.auth.token',
          supabaseUrl ? `sb-${supabaseUrl.split('//')[1]?.split('.')[0]}-auth-token` : '',
        ].filter(Boolean);
        possibleKeys.forEach((key) => {
          if (localStorage.getItem(key)) {
            localStorage.removeItem(key);
            console.log('🗑️ Removed auth token from localStorage:', key);
          }
        });
        Object.keys(localStorage).forEach((key) => {
          if (key.includes('supabase') && key.includes('auth')) {
            localStorage.removeItem(key);
            console.log('🗑️ Removed auth-related key from localStorage:', key);
          }
        });
      } catch (storageError) {
        console.warn('⚠️ Error clearing localStorage:', storageError);
      }
      console.log('✅ Sign out complete');
    } catch (error: unknown) {
      console.error('❌ Unexpected error during sign out:', error);
      setUser(null);
      setProfile(null);
      setLoading(false);
      userRef.current = null;
      profileRef.current = null;
      lastFetchedUserId.current = null;
      profileFetchInProgress.current = false;
    }
  }, []);

  const fetchUserProfile = useCallback(async (userId: string, retryCount = 0, force = false): Promise<void> => {
    const maxRetries = 3; // Reduced from 5 to 3
    const retryDelay = 500; // Reduced from 1000ms to 500ms
    const queryTimeout = 5000; // Increased from 3000ms to 5000ms
    
    // Prevent rival root fetches; allow retries of the same in-flight fetch
    if (
      shouldSkipDuplicateProfileFetch({
        force,
        retryCount,
        inProgress: profileFetchInProgress.current,
        lastFetchedUserId: lastFetchedUserId.current,
        userId,
      })
    ) {
      console.log('⏭️ Profile fetch already in progress for this user, skipping...');
      return;
    }
    
    // If profile already exists for this user and not a retry, skip
    if (!force && retryCount === 0 && profileRef.current && profileRef.current.id === userId) {
      console.log('✅ Profile already loaded for this user, skipping fetch');
      return;
    }

    if (retryCount === 0) {
      profileFetchInProgress.current = true;
      lastFetchedUserId.current = userId;
    }
    
    try {
      console.log(`🔍 Fetching user profile for: ${userId} (attempt ${retryCount + 1}/${maxRetries + 1})`);
      
      // On first attempt, wait a bit for session to stabilize (auth.uid() needs time to be available)
      if (retryCount === 0) {
        console.log('⏳ Waiting 200ms for session to stabilize before first query...');
        await new Promise(resolve => setTimeout(resolve, 200));
      }
      
      // First, try using Supabase client directly (handles RLS better)
      let profile = null;
      let error = null;
      
      // Try by ID first using Supabase client with timeout
      console.log('📡 Attempting to fetch profile by ID from users table...');
      console.log('🔑 User ID from parameter:', userId);
      
      try {
        const startTime = Date.now();
        console.log('⏳ Executing query: SELECT * FROM users WHERE id =', userId);
        console.log(`⏱️ Query timeout set to ${queryTimeout}ms...`);
        
        // Create query promise
        const queryPromise = supabase
          .from('users')
          .select('*')
          .eq('id', userId)
          .maybeSingle();
        
        // Create timeout promise that will definitely fire
        let timeoutId: NodeJS.Timeout;
        const timeoutPromise = new Promise<never>((_, reject) => {
          timeoutId = setTimeout(() => {
            console.error(`⏱️ ⚠️ Profile query TIMED OUT after ${queryTimeout}ms`);
            if (retryCount === 0) {
              console.error('💡 First attempt timed out - this might be a session timing issue');
              console.error('💡 Retrying with exponential backoff...');
            }
            reject(new Error(`Profile fetch timeout after ${queryTimeout}ms`));
          }, queryTimeout);
        });
        
        console.log('🏁 Starting Promise.race between query and timeout...');
        let result: { data: any; error: any };
        try {
          result = await Promise.race([queryPromise, timeoutPromise]) as { data: any; error: any };
          clearTimeout(timeoutId!);
          const elapsed = Date.now() - startTime;
          console.log(`✅ Query completed in ${elapsed}ms`);
        } catch (timeoutError: any) {
          clearTimeout(timeoutId!);
          console.error('❌ Query timeout:', timeoutError?.message);
          throw timeoutError;
        }
        
        const { data: profileById, error: errorById } = result;
        
        if (errorById) {
          console.error('❌ Error fetching profile by ID:', errorById);
          console.error('Error code:', errorById.code);
          console.error('Error message:', errorById.message);
          console.error('Error details:', JSON.stringify(errorById, null, 2));
          
          // Check if it's an RLS error
          if (errorById.code === '42501' || errorById.message?.includes('permission') || errorById.message?.includes('policy')) {
            console.error('🚫 This looks like an RLS (Row Level Security) error!');
            console.error('💡 The user might not have permission to read their own profile.');
            console.error('💡 Check if the RLS policy "Users can read own profile" exists and allows: auth.uid() = id');
          }
          
          error = errorById;
        } else if (profileById) {
          profile = profileById;
          console.log('✅ Profile found by ID using Supabase client:', {
            id: profile.id,
            email: profile.email,
            first_name: profile.first_name,
            last_name: profile.last_name,
            role: profile.role
          });
        } else {
          console.log('ℹ️ No profile found by ID - query returned no rows');
          console.log('💡 This means the user does not exist in the users table');
          console.log('💡 The profile needs to be created or the user ID might be different');
        }
      } catch (timeoutError: any) {
        console.error(`⏱️ Profile fetch by ID timed out after ${queryTimeout}ms`);
        error = timeoutError;
      }
      
      // If not found by ID, try by email
      if (!profile && !error) {
        console.log('📧 Profile not found by ID, trying to fetch by email...');
        try {
          // Get auth user with timeout
          const authUserTimeout = new Promise((_, reject) => 
            setTimeout(() => reject(new Error('Auth user fetch timeout')), 5000)
          );
          const authUserResult = await Promise.race([supabase.auth.getUser(), authUserTimeout]) as { data: { user: any }; error: any };
          const { data: { user: authUser }, error: authError } = authUserResult;
          
          if (authError) {
            console.error('❌ Error getting auth user:', authError);
          } else if (authUser?.email) {
            console.log(`📧 Trying to find profile by email: ${authUser.email}`);
            
            // Email query with timeout
            const emailQueryPromise = supabase
              .from('users')
              .select('*')
              .eq('email', authUser.email)
              .maybeSingle();
            
            const emailTimeout = new Promise((_, reject) => 
              setTimeout(() => reject(new Error('Email profile fetch timeout')), 10000)
            );
            
            const emailResult = await Promise.race([emailQueryPromise, emailTimeout]) as { data: any; error: any };
            const { data: profileByEmail, error: errorByEmail } = emailResult;
            
            if (errorByEmail) {
              console.warn('⚠️ Error fetching by email:', errorByEmail);
              error = errorByEmail;
            } else if (profileByEmail) {
              profile = profileByEmail;
              console.log('✅ Profile found by email using Supabase client!');
            } else {
              console.log('ℹ️ No profile found by email either');
            }
          }
        } catch (emailError) {
          console.warn('⚠️ Error in email fetch process:', emailError);
          error = emailError as any;
        }
      }
      
      // Fallback to REST API if Supabase client fails
      if (!profile && !error) {
        console.log('Trying REST API as fallback...');
        const apiResult = await supabaseApi.getUserProfile(userId);
        if (apiResult.data && !apiResult.error) {
          const apiProfile = Array.isArray(apiResult.data) ? apiResult.data[0] : apiResult.data;
          if (apiProfile) {
            profile = apiProfile;
            console.log('Profile found using REST API');
          }
        }
      }
        
        if (profile) {
          console.log('Profile found:', profile);
          const accountStatus = profile.account_status as 'active' | 'deleted' | undefined;
          const deletedAt = profile.deleted_at as string | null | undefined;
          if (accountStatus === 'deleted' || deletedAt) {
            console.warn('🚫 Account is deleted or closed; clearing session');
            await clearAuthSession();
            return;
          }
          const userProfile = mapRowToUserProfile({
            ...profile,
            account_status: accountStatus ?? 'active',
            deleted_at: deletedAt ?? null,
          });
          setProfile(userProfile);
          console.log('✅ Profile set successfully');
          return;
      }

      // If profile not found, try to create it
      if (!profile && !error) {
          console.warn('No profile found for user:', userId);
          
          // Retry if profile doesn't exist yet (might be creating via trigger)
          if (retryCount < maxRetries) {
            console.log(`Profile not found, retrying in ${retryDelay}ms (trigger might still be creating it)...`);
            await new Promise(resolve => setTimeout(resolve, retryDelay));
            return await fetchUserProfile(userId, retryCount + 1, force);
          }
          
        // Try to create a profile if it doesn't exist
          try {
            const { data: { user: currentUser } } = await supabase.auth.getUser();
            if (currentUser && currentUser.id === userId) {
            console.log('Attempting to create missing profile for returning user...');
              const meta = (currentUser.user_metadata || {}) as Record<string, unknown>;
              const { first_name, last_name } = nameFieldsFromAuthMetadata(meta, currentUser.email);
              const profileData = {
                id: currentUser.id,
                email: currentUser.email!,
                first_name,
                last_name,
                role: (meta.role as string) || 'member',
              timezone: 'America/New_York',
            };
            
            console.log('Profile data to create:', profileData);
            
            // Try using Supabase client first - use insert with conflict handling
            // If profile exists but RLS blocks it, we'll handle that
            const { data: createdProfile, error: createError } = await supabase
              .from('users')
              .insert(profileData)
              .select()
              .single();
            
            if (createdProfile && !createError) {
              console.log('Profile created/updated successfully using Supabase client');
                setProfile(mapRowToUserProfile(createdProfile));
                return;
              } else {
              console.warn('Failed to create profile using Supabase client:', createError);
              // If RLS is blocking, try using REST API with user's auth token
              console.log('Trying REST API as fallback...');
              const { data: profileResult, error: profileError } = await supabaseApi.insertUserProfile(profileData);
              if (!profileError && profileResult) {
                console.log('Profile created successfully using REST API');
                const apiProfile = Array.isArray(profileResult) ? profileResult[0] : profileResult;
                setProfile(mapRowToUserProfile(apiProfile));
                return;
              } else {
                console.error('Failed to create profile via REST API:', profileError);
                // Last resort: try to fetch again after a short delay (maybe trigger created it)
                console.log('Waiting a moment and retrying fetch...');
                await new Promise(resolve => setTimeout(resolve, 1000));
                if (retryCount < 2) {
                  return await fetchUserProfile(userId, retryCount + 1, force);
                }
              }
              }
            }
          } catch (createError) {
            console.error('Error creating profile:', createError);
          // If creation fails, try one more fetch in case trigger created it
          if (retryCount < 2) {
            console.log('Retrying profile fetch after creation attempt...');
            await new Promise(resolve => setTimeout(resolve, 1000));
            return await fetchUserProfile(userId, retryCount + 1, force);
          }
        }
      }

      if (error) {
        console.error('Error fetching user profile:', error);
        console.error('Error details:', JSON.stringify(error, null, 2));
        
        // Retry if we haven't exceeded max retries
        if (retryCount < maxRetries) {
          console.log(`Retrying profile fetch in ${retryDelay}ms...`);
          await new Promise(resolve => setTimeout(resolve, retryDelay));
          return await fetchUserProfile(userId, retryCount + 1, force);
        }
      }
      
      setProfile(null);
    } catch (error) {
      console.error('Error in fetchUserProfile:', error);
      
      // Retry on error with exponential backoff
      if (retryCount < maxRetries) {
        const backoffDelay = retryDelay * Math.pow(2, retryCount); // Exponential backoff: 500ms, 1000ms, 2000ms
        console.log(`⏳ Retrying in ${backoffDelay}ms (exponential backoff, attempt ${retryCount + 2}/${maxRetries + 1})...`);
        await new Promise(resolve => setTimeout(resolve, backoffDelay));
        return await fetchUserProfile(userId, retryCount + 1, force);
      }
      
      console.error('❌ Profile fetch failed after all retries');
      setProfile(null);
    } finally {
      if (retryCount === 0) {
        profileFetchInProgress.current = false;
      }
    }
  }, [clearAuthSession]);

  // Assign fetchUserProfile to ref so it can be called from other places
  useEffect(() => {
    fetchUserProfileRef.current = fetchUserProfile;
  }, [fetchUserProfile]);

  useEffect(() => {
    // Prevent multiple initializations
    if (authInitialized.current) {
      console.log('⏭️ Auth already initialized, skipping...');
      return;
    }
    
    // Get initial session and handle email verification redirects
    const initializeAuth = async () => {
      authInitialized.current = true;
      let profileFetchTimeout: NodeJS.Timeout | null = null;
      
      try {
        console.log('🔍 Initializing auth - checking for persisted session...');
        
        // Check localStorage directly to see if session exists
        // Supabase uses different storage keys depending on version
        const supabaseUrl = process.env.REACT_APP_SUPABASE_URL || '';
        const possibleKeys = [
          'supabase.auth.token',
          supabaseUrl ? `sb-${supabaseUrl.split('//')[1]?.split('.')[0]}-auth-token` : '',
          supabaseUrl ? `sb-${supabaseUrl.replace(/https?:\/\//, '').replace(/\./g, '-')}-auth-token` : ''
        ].filter(key => key); // Remove empty strings
        
        console.log('📦 Checking localStorage for session...');
        possibleKeys.forEach(key => {
          const value = localStorage.getItem(key);
          if (value) {
            console.log(`✅ Found session in localStorage with key: ${key}`, value.substring(0, 50) + '...');
          } else {
            console.log(`❌ No session found with key: ${key}`);
          }
        });
        
        // Also check all localStorage keys that might be related
        console.log('📋 All localStorage keys:', Object.keys(localStorage).filter(k => k.includes('supabase') || k.includes('auth')));
        
        // Set a timeout to ensure loading state resolves even if Supabase is slow
        const timeoutId = setTimeout(() => {
          console.warn('⚠️ Auth initialization timeout - setting loading to false');
          setLoading(false);
        }, 10000); // 10 second timeout for initial session check

        // Signup confirmation: never keep a prior account's session on this page load.
        // detectSessionInUrl may already have applied link tokens — if so, keep that session.
        // Otherwise clear storage and apply captured tokens. If we cannot establish the
        // confirmed user, stay signed out (do not fall back to Ipek/other prior session).
        if (wasSignupConfirmationRedirect()) {
          const signupConfirmTokens = getSignupConfirmTokens();
          const hadTokens = !!signupConfirmTokens;
          const expectedUserId = getExpectedSignupConfirmUserId();
          console.log('📧 Signup confirmation redirect — reconciling session', {
            hasTokens: hadTokens,
            expectedUserId,
          });

          // Ensure client finished URL/storage init before we touch the session
          await supabase.auth.getSession();

          const { data: preData } = await supabase.auth.getSession();
          const preUserId = preData.session?.user?.id ?? null;

          if (expectedUserId && preUserId === expectedUserId) {
            console.log('✅ Confirmation session already matches link user — keeping it');
            clearCapturedSignupConfirmTokens();
          } else {
            console.log('🧹 Clearing prior session before confirmation handoff', { preUserId, expectedUserId });
            try {
              await supabase.auth.signOut({ scope: 'local' });
            } catch (priorSignOutErr: unknown) {
              const msg = priorSignOutErr instanceof Error ? priorSignOutErr.message : String(priorSignOutErr);
              console.warn('⚠️ Prior-session clear on signup confirm (continuing):', msg);
            }
            setUser(null);
            setProfile(null);
            userRef.current = null;
            profileRef.current = null;
            lastFetchedUserId.current = null;
            profileFetchInProgress.current = false;

            if (signupConfirmTokens) {
              const { error: setSessionError } = await supabase.auth.setSession({
                access_token: signupConfirmTokens.access_token,
                refresh_token: signupConfirmTokens.refresh_token,
              });
              if (setSessionError) {
                console.error('❌ Failed to apply signup confirmation session:', setSessionError.message);
              } else {
                console.log('✅ Signup confirmation session applied');
              }
            } else {
              console.warn('⚠️ Signup confirmation redirect without tokens — staying signed out');
            }
            clearCapturedSignupConfirmTokens();
          }

          try {
            const cleanPath = `${window.location.pathname}${window.location.search}`;
            window.history.replaceState(null, '', cleanPath);
          } catch {
            /* ignore */
          }

          // Final guard: never keep a session that isn't the confirmed user
          const { data: postData } = await supabase.auth.getSession();
          const postUserId = postData.session?.user?.id ?? null;
          const sessionIsWrongUser = !!(expectedUserId && postUserId && postUserId !== expectedUserId);
          const sessionWithoutLinkIdentity = !expectedUserId && !!postUserId && !hadTokens;
          if (sessionIsWrongUser || sessionWithoutLinkIdentity) {
            console.error('❌ Rejecting non-confirmation session after signup redirect', {
              postUserId,
              expectedUserId,
              hadTokens,
            });
            try {
              await supabase.auth.signOut({ scope: 'local' });
            } catch {
              /* ignore */
            }
            setUser(null);
            setProfile(null);
            userRef.current = null;
            profileRef.current = null;
            lastFetchedUserId.current = null;
            profileFetchInProgress.current = false;
          }
        }

        // Handle email verification callback if present
        console.log('🔐 Calling supabase.auth.getSession()...');
        const { data, error } = await supabase.auth.getSession();
        
        clearTimeout(timeoutId);
        
        console.log('📋 Session retrieval result:', {
          hasSession: !!data.session,
          hasUser: !!data.session?.user,
          userId: data.session?.user?.id,
          error: error?.message,
          expiresAt: data.session?.expires_at
        });
        
        if (error) {
          console.error('❌ Error getting session:', error);
          console.error('Error details:', JSON.stringify(error, null, 2));
          setLoading(false);
          return;
        }
        
        if (data.session) {
          // Check if session is expired
          const now = Math.floor(Date.now() / 1000);
          const expiresAt = data.session.expires_at || 0;
          if (expiresAt < now) {
            console.warn('⚠️ Session expired. Expires at:', new Date(expiresAt * 1000), 'Current time:', new Date());
          } else {
            console.log('✅ Session is valid. Expires at:', new Date(expiresAt * 1000));
          }
        }
        
        setUser(data.session?.user ?? null);
        
        if (data.session?.user) {
          console.log('✅ User authenticated:', data.session.user.id, data.session.user.email);
          // Set loading to false immediately after getting session
          // Profile can load in background, but don't block on it
          setLoading(false);
          
          // Fetch profile in background with a hard timeout
          // If it takes more than 5 seconds, give up and let user access the app
          const fetchProfile = fetchUserProfileRef.current || fetchUserProfile;
          const profileTimeoutId = setTimeout(() => {
            console.warn('⚠️ Profile fetch taking too long, continuing without profile');
          }, 5000);
          fetchProfile(data.session.user.id)
            .then(() => clearTimeout(profileTimeoutId))
            .catch((err) => {
              clearTimeout(profileTimeoutId);
              console.error('❌ Profile fetch failed:', err);
              // Don't throw - allow app to continue without profile
            });
        } else {
          console.log('ℹ️ No session found - user not authenticated');
          // No session, clear profile
          setProfile(null);
          setLoading(false);
        }
      } catch (error) {
        console.error('❌ Error initializing auth:', error);
        if (profileFetchTimeout) clearTimeout(profileFetchTimeout);
        setLoading(false);
      }
    };

    initializeAuth();

    // Listen for auth changes (only set up once)
    if (subscriptionSetup.current) {
      console.log('⏭️ Auth subscription already set up, skipping...');
      return;
    }
    
    let subscription: any;
    try {
      subscriptionSetup.current = true;
      const { data: { subscription: sub } } = supabase.auth.onAuthStateChange(
        (event, session) => {
          console.log('Auth state changed:', event, session?.user?.id);
          
          // Skip TOKEN_REFRESHED events - these happen on visibility change and don't need processing
          if (event === 'TOKEN_REFRESHED') {
            console.log('⏭️ Token refreshed (visibility change), skipping');
            return;
          }
          
          // Get current state BEFORE any processing
          const newUserId = session?.user?.id;
          const currentUserId = userRef.current?.id;
          const currentProfile = profileRef.current;
          
          // Skip SIGNED_IN events if we already have the same user and profile
          // This prevents re-processing on visibility changes (tab switch, screen lock)
          if (event === 'SIGNED_IN' && newUserId === currentUserId && currentProfile && currentProfile.id === newUserId) {
            console.log('⏭️ SIGNED_IN event for same user with existing profile, skipping completely');
            return;
          }
          
          // Skip if same user and profile already loaded (prevents re-fetch on tab switch)
          if (newUserId === currentUserId && currentProfile && currentProfile.id === newUserId) {
            console.log('⏭️ Same user and profile already loaded, skipping profile fetch');
            // Don't update any state - keep everything as is
            return;
          }
          
          // Only update user if it actually changed — clear stale profile immediately
          // so UI cannot greet the previous account during the swap.
          if (newUserId !== currentUserId) {
            setProfile(null);
            profileRef.current = null;
            setUser(session?.user ?? null);
          }
          
          // Handle sign out event explicitly
          if (event === 'SIGNED_OUT' || !session) {
            console.log('🚪 Sign out detected, clearing state');
            setUser(null);
            setProfile(null);
            setLoading(false);
            userRef.current = null;
            profileRef.current = null;
            lastFetchedUserId.current = null;
            profileFetchInProgress.current = false;
            return;
          }
          
          if (session?.user) {
            const needsProfile =
              newUserId !== currentUserId || !currentProfile || currentProfile.id !== newUserId;
            if (needsProfile) {
              const userIdToFetch = session.user.id;
              // Must not await supabase.from() inside this callback — GoTrue holds
              // navigatorLock and the profile SELECT deadlocks (5s timeouts).
              scheduleAfterAuthCallback(() => {
                const fetchProfile = fetchUserProfileRef.current;
                if (!fetchProfile) {
                  setLoading(false);
                  return;
                }
                fetchProfile(userIdToFetch, 0, true)
                  .catch((profileError) => {
                    console.error('Error fetching user profile in auth change:', profileError);
                  })
                  .finally(() => {
                    setLoading(false);
                  });
              });
            }
          } else {
            // User signed out
            setProfile(null);
            profileFetchInProgress.current = false;
            lastFetchedUserId.current = null;
            setLoading(false);
          }
        }
      );
      subscription = sub;
    } catch (error) {
      console.error('Error setting up auth state listener:', error);
      setLoading(false);
    }

    return () => {
      if (subscription) {
        subscription.unsubscribe();
        subscriptionSetup.current = false;
      }
    };
  }, []); // Empty dependency array - only run once on mount

  const signUp = async (email: string, password: string, userData: any) => {
    try {
      const role = userData?.role === 'trainer' ? 'trainer' : 'member';
      const redirectUrl = getSignupConfirmRedirectUrl() || undefined;
      const { first: splitFirst, last: splitLast } = splitFullNameLastWordIsLastName(String(userData.name || ''));
      const inviteCode = peekMemberInviteCode();
      const { data, error } = await supabase.auth.signUp({
        email,
        password,
        options: {
          emailRedirectTo: redirectUrl,
          data: {
            first_name: userData.first_name ?? splitFirst ?? '',
            last_name: userData.last_name ?? splitLast ?? '',
            role: role,
            country: userData.country ?? null,
            referral_source: userData.referral_source ?? null,
            marketing_notifications_enabled: Boolean(userData.marketing_notifications_enabled),
            ...(inviteCode ? { invite_code: inviteCode } : {}),
          },
        },
      });

      if (error) {
        console.log('Signup error:', error);
        return { user: null, error };
      }

      console.log('Signup successful:', data);

      if (data.user) {
        console.log('User created:', data.user.id);
        // Profile will be automatically created by database trigger
        // Wait a moment for the trigger to execute, then fetch the profile
        await new Promise(resolve => setTimeout(resolve, 500));

        try {
          await supabase
            .from('users')
            .update({
              marketing_notifications_enabled: Boolean(userData.marketing_notifications_enabled),
            })
            .eq('id', data.user.id);
          await fetchUserProfile(data.user.id);
        } catch (profileError) {
          console.warn('Profile not immediately available, will be fetched on next auth check:', profileError);
        }
        // Attribution applied by handle_new_user from metadata — clear stash after attempt.
        clearMemberInviteCode();
      }

      return { user: data.user, error: null };
    } catch (error) {
      console.error('Unexpected error in signUp:', error);
      return { user: null, error: error as AuthError };
    }
  };

  const signIn = async (email: string, password: string) => {
    try {
      console.log('🔐 Attempting to sign in with email:', email);
      
      // Check if Supabase client is properly initialized
      if (!supabase) {
        console.error('❌ Supabase client is not initialized');
        return { 
          user: null, 
          error: { 
            message: 'Authentication service is not available. Please check your configuration.' 
          } as AuthError 
        };
      }

    console.log('📡 Calling supabase.auth.signInWithPassword...');
    
    // Add a timeout wrapper for the actual Supabase call
    const signInPromise = supabase.auth.signInWithPassword({
      email,
      password,
    });
    
    const timeoutPromise = new Promise((_, reject) => {
      setTimeout(() => {
        reject(new Error('Sign in request timed out after 8 seconds'));
      }, 8000);
    });
    
    const { data, error } = await Promise.race([signInPromise, timeoutPromise]) as any;
    console.log('📡 Sign in response received:', { hasUser: !!data?.user, hasError: !!error });

      if (error) {
        console.error('❌ Sign in error:', error);
        return { user: null, error };
      }

      if (data?.user) {
        console.log('✅ Sign in successful for user:', data.user.id);
        console.log('📦 Session data:', {
          hasSession: !!data.session,
          expiresAt: data.session?.expires_at ? new Date(data.session.expires_at * 1000) : null,
          accessToken: data.session?.access_token ? 'Present' : 'Missing'
        });
        
        // Check if session was stored after a brief delay
        setTimeout(() => {
          const stored = localStorage.getItem('supabase.auth.token');
          console.log('💾 Session storage check after sign in:', stored ? '✅ Stored' : '❌ Not stored');
          if (!stored) {
            // Check all possible keys
            const allKeys = Object.keys(localStorage);
            const authKeys = allKeys.filter(k => k.includes('supabase') || k.includes('auth'));
            console.log('🔑 All auth-related localStorage keys:', authKeys);
          }
        }, 500);
        // Fetch profile after GoTrue releases its lock (signInWithPassword holds it
        // until this call stack finishes; awaiting SELECT here caused timeouts).
        await new Promise<void>((resolve) => {
          scheduleAfterAuthCallback(() => resolve());
        });
        try {
          await fetchUserProfile(data.user.id, 0, true);
        } catch (profileError) {
          console.warn('⚠️ Profile fetch error after sign in (non-critical):', profileError);
        }
      }

      return { user: data?.user ?? null, error: null };
    } catch (err: any) {
      console.error('❌ Unexpected error in signIn:', err);
      return { 
        user: null, 
        error: { 
          message: err.message || 'An unexpected error occurred during sign in. Please try again.' 
        } as AuthError 
      };
    }
  };

  const signOut = clearAuthSession;

  /** Deletes the member account on the server only; caller should sign out and redirect (e.g. after a short UI message). */
  const deleteMemberAccount = async (): Promise<{ error: string | null }> => {
    return deleteMemberAccountRequest();
  };

  const markAsLoggedIn = useCallback(async () => {
    if (!user || !profile || profile.has_logged_in) return;
    try {
      await supabase.from('users').update({ has_logged_in: true }).eq('id', user.id);
      setProfile(prev => prev ? { ...prev, has_logged_in: true } : prev);
    } catch (err) {
      console.warn('Failed to mark user as logged in:', err);
    }
  }, [user, profile]);

  const updateProfile = async (updates: Partial<UserProfile>) => {
    if (!user) return;

    const current = profileRef.current;
    const patch: Record<string, unknown> = { ...updates };

    const touchesName =
      typeof updates.first_name === 'string' || typeof updates.last_name === 'string';

    if (touchesName) {
      const first =
        typeof updates.first_name === 'string'
          ? updates.first_name.trim()
          : (current?.first_name ?? '').trim();
      const last =
        typeof updates.last_name === 'string'
          ? updates.last_name.trim()
          : (current?.last_name ?? '').trim();

      patch.first_name = first;
      patch.last_name = last;
    }

    const { error } = await supabaseApi.updateUserProfile(user.id, patch);

    if (error) {
      console.error('Error updating profile:', error);
      throw new Error(error.message);
    }

    if (touchesName) {
      const first = String(patch.first_name ?? '');
      const last = String(patch.last_name ?? '');
      const { data: authData, error: authMetaError } = await supabase.auth.updateUser({
        data: {
          first_name: first,
          last_name: last,
        },
      });
      if (authMetaError) {
        console.warn('Auth user_metadata sync after name update failed:', authMetaError.message);
      } else if (authData.user) {
        setUser(authData.user);
      }
    }

    await fetchUserProfile(user.id);
  };

  const value: AuthContextType = {
    user,
    profile,
    loading,
    signUp,
    signIn,
    signOut,
    deleteMemberAccount,
    updateProfile,
    markAsLoggedIn,
  };

  return (
    <AuthContext.Provider value={value}>
      {children}
    </AuthContext.Provider>
  );
}; 